import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';
import { formatPreciseDuration, formatUtcTimestampPrecise } from '~/utils/date';

import type { HistoryEvent } from '../../types/workflow-inspection.types';
import { type HistorySpan, humanizeEventType } from '../../utils/history-spans';
import { JsonViewer } from '../json-viewer';

export type EventTableView = 'all' | 'compact' | 'json';
const PAGE_SIZES = [25, 50, 100] as const;
/** Rows scroll inside a 600px box (header stays pinned) instead of growing the page. */
const TABLE_SCROLL = 'max-h-[600px]';
const STICKY_HEADER = 'bg-background sticky top-0 z-10 shadow-[0_1px_0_var(--border)]';

/** Event types whose row is tinted red (Temporal highlights failures the same way). */
function isFailureEvent(event: HistoryEvent): boolean {
  return event.failure !== null || /Failed$|TimedOut$|Terminated$/.test(event.eventType);
}

interface Props {
  /** Already filtered + ordered for display. */
  events: HistoryEvent[];
  /** Compact rows (activity/timer/child spans), already ordered. */
  spans: HistorySpan[];
  view: EventTableView;
  onViewChange: (view: EventTableView) => void;
  onSelectEvent: (eventId: number) => void;
}

/**
 * The Event History table in three views — All (one row per raw event),
 * Compact (one row per activity/timer/child span), JSON (the filtered events
 * as JSON) — with client-side paging over what's already loaded.
 */
export function EventTable({ events, spans, view, onViewChange, onSelectEvent }: Props) {
  const { t } = useTranslation('workflow-inspection');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const rowCount = view === 'compact' ? spans.length : events.length;
  const pages = Math.max(1, Math.ceil(rowCount / pageSize));
  // Clamp rather than reset in an effect: a filter that shrinks the list just
  // lands on its last page.
  const current = Math.min(page, pages);
  const from = (current - 1) * pageSize;

  return (
    <div className="bg-background rounded-lg border" data-testid="workflow-inspector-history-table">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-2">
        <SegmentedTabs
          value={view}
          onChange={onViewChange}
          ariaLabel={t('history.views.ariaLabel')}
          testId="workflow-inspector-history-view-tabs"
          className="bg-selected-row border-0"
          options={[
            { value: 'all', label: t('history.views.all') },
            { value: 'compact', label: t('history.views.compact') },
            { value: 'json', label: t('history.views.json') },
          ]}
        />
      </div>

      {view === 'json' ? (
        <div className="p-2">
          <JsonViewer
            value={events}
            title={t('history.title')}
            initialDepth={1}
            testId="workflow-inspector-history-json"
          />
        </div>
      ) : rowCount === 0 ? (
        <p
          className="text-muted-foreground p-6 text-center text-sm"
          data-testid="workflow-inspector-history-table-empty"
        >
          {t('history.table.noMatches')}
        </p>
      ) : view === 'all' ? (
        <Table
          containerClassName={TABLE_SCROLL}
          data-testid="workflow-inspector-history-events-table"
        >
          <TableHeader className={STICKY_HEADER}>
            <TableRow>
              <TableHead className="w-20">{t('history.table.eventId')}</TableHead>
              <TableHead className="w-56">{t('history.table.timestamp')}</TableHead>
              <TableHead className="w-64">{t('history.table.eventType')}</TableHead>
              <TableHead>{t('history.table.details')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.slice(from, from + pageSize).map((e) => (
              <TableRow
                key={e.eventId}
                data-testid={`workflow-inspector-history-event-row-${String(e.eventId)}`}
                className={cn(
                  'cursor-pointer',
                  isFailureEvent(e) && 'bg-destructive/10 hover:bg-destructive/15',
                )}
                onClick={() => {
                  onSelectEvent(e.eventId);
                }}
              >
                <TableCell className="text-primary font-mono text-xs underline-offset-2 hover:underline">
                  {e.eventId}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs tabular-nums">
                  {formatUtcTimestampPrecise(e.eventTime)}
                </TableCell>
                <TableCell className="text-foreground text-sm font-medium">
                  {humanizeEventType(e.eventType)}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs break-words whitespace-normal">
                  {e.summary ?? ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table
          containerClassName={TABLE_SCROLL}
          data-testid="workflow-inspector-history-spans-table"
        >
          <TableHeader className={STICKY_HEADER}>
            <TableRow>
              <TableHead>{t('history.table.span')}</TableHead>
              <TableHead className="w-32">{t('history.table.kind')}</TableHead>
              <TableHead className="w-28">{t('history.table.status')}</TableHead>
              <TableHead className="w-24">{t('history.table.attempts')}</TableHead>
              <TableHead className="w-28">{t('history.table.start')}</TableHead>
              <TableHead className="w-28">{t('history.table.duration')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spans.slice(from, from + pageSize).map((s) => (
              <TableRow
                key={s.key}
                data-testid={`workflow-inspector-history-span-row-${String(s.key)}`}
                className={cn(
                  'cursor-pointer',
                  s.status === 'failed' && 'bg-destructive/10 hover:bg-destructive/15',
                )}
                onClick={() => {
                  onSelectEvent(s.key);
                }}
              >
                <TableCell
                  className="text-foreground text-sm font-medium"
                  title={s.failureMessage ?? ''}
                >
                  {s.label}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {t(`history.spanKind.${s.kind}`)}
                </TableCell>
                <TableCell className="text-xs">{t(`history.spanStatus.${s.status}`)}</TableCell>
                <TableCell className="text-xs tabular-nums">{s.attempts ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground text-xs tabular-nums">
                  +{formatPreciseDuration(s.startMs)}
                </TableCell>
                <TableCell className="text-xs tabular-nums">
                  {s.endMs === null ? '—' : formatPreciseDuration(s.endMs - s.startMs)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {view !== 'json' && rowCount > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t p-2 text-sm">
          <span className="text-muted-foreground text-xs">{t('history.table.pageSize')}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger
              className="h-8 w-20 cursor-pointer"
              data-testid="workflow-inspector-history-page-size-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid="workflow-inspector-history-page-size-content">
              {PAGE_SIZES.map((size) => (
                <SelectItem
                  key={size}
                  value={String(size)}
                  className="cursor-pointer"
                  data-testid={`workflow-inspector-history-page-size-option-${String(size)}`}
                >
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span
            className="text-muted-foreground text-xs tabular-nums"
            data-testid="workflow-inspector-history-page-value"
          >
            {t('history.table.pageOf', { page: current, pages })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('history.table.previous')}
            data-testid="workflow-inspector-history-prev-page-button"
            disabled={current <= 1}
            onClick={() => {
              setPage(current - 1);
            }}
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('history.table.next')}
            data-testid="workflow-inspector-history-next-page-button"
            disabled={current >= pages}
            onClick={() => {
              setPage(current + 1);
            }}
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
